import { request } from '@/utils/http'

const DEFAULT_GATEWAY_BASE = '/universal'
const RAW_GATEWAY_BASE = import.meta.env.VITE_UNIVERSAL_API_BASE
  || import.meta.env.VITE_APPROVAL_API_BASE
  || DEFAULT_GATEWAY_BASE
const GATEWAY_BASE = RAW_GATEWAY_BASE.endsWith('/') ? RAW_GATEWAY_BASE.slice(0, -1) : RAW_GATEWAY_BASE
const API_BASE = GATEWAY_BASE.endsWith('/api') ? GATEWAY_BASE : `${GATEWAY_BASE}/api`
const SERVICE_API_BASE = API_BASE.endsWith('/api') ? API_BASE.slice(0, -4) : GATEWAY_BASE
const AUTO_RESOLVED_ACTOR_SENTINEL = '0'
const NO_NOTIFIER_TYPE = -1
const WORKFLOW_HIDDEN_DISABLED_STATE = -8

export interface ApprovalPageResponse<T> {
  pageNum: number
  pageSize: number
  size: number
  total: number
  pages: number
  list: T[]
}

export interface ApprovalOption {
  label: string
  value: string
}

export interface ApprovalProcessQuery {
  pageNum: number
  pageSize: number
  createBeginDate?: string
  createEndDate?: string
  name?: string
  documentConfigId?: string | null
  officeIdList?: string[]
  runningState?: number | null
  workflowGroupId?: string | null
}

interface ApprovalProcessListItemRaw {
  id?: string | number | null
  name?: string | null
  officeIds?: string | Array<string | number> | null
  documentConfigId?: string | number | null
  runningState?: number | null
  remark?: string | null
  modifierId?: string | number | null
  modifyId?: string | number | null
  modifyid?: string | number | null
  modifyBy?: string | number | null
  modifiedBy?: string | number | null
  lastModified?: string | null
  creatorId?: string | number | null
  createBy?: string | number | null
  createby?: string | number | null
  createdBy?: string | number | null
  createTime?: string | null
  formSubtype?: number | null
  templateUuid?: string | null
  exportType?: string | null
}

export interface ApprovalProcessListItem {
  id: string
  name: string
  officeIds: string[]
  officeIdsText: string
  documentConfigId: string
  runningState: number
  remark: string
  modifiedBy: string
  lastModified: string
  createdBy: string
  createTime: string
  formSubtype: number | null
  templateUuid: string
  exportType: string
}

interface ApprovalRuleDefinitionRaw {
  tenantId?: string | number | null
  column?: string | null
  alias?: string | null
  operator?: string | null
  value?: string | null
  customizeAllowed?: boolean | null
}

export interface ApprovalRuleValueOption {
  label: string
  value: string
}

export interface ApprovalRuleOperatorOption {
  label: string
  value: string
}

export interface ApprovalRuleDefinition {
  column: string
  label: string
  operators: string[]
  operatorOptions: ApprovalRuleOperatorOption[]
  options: ApprovalRuleValueOption[]
  customizeAllowed: boolean
  valueType: string
  rawOperator: string
  rawValue: string
}

interface ApprovalNodeCriteriaRaw {
  criteria?: string | null
  operator?: string | null
  checkValue?: string | null
  relation?: number | null
}

export interface ApprovalNodeCriteriaForm {
  criteria: string
  operator: string
  checkValue: string
  relation: number
}

interface ApprovalWorkflowNodeRaw {
  nodeName?: string | null
  nodeType?: number | null
  startNode?: boolean | null
  closedNode?: boolean | null
  editable?: boolean | null
  remarkRequired?: boolean | null
  cancelAllowed?: boolean | null
  transferAllowed?: boolean | null
  actorType?: number | null
  actorId?: Array<string | number> | null
  notifierType?: number | null
  notifierId?: Array<string | number> | null
  preCriteriaList?: ApprovalNodeCriteriaRaw[] | null
  dashboardName?: string | null
  dashboardId?: string | number | null
}

export interface ApprovalWorkflowNodeForm {
  id: string
  nodeName: string
  nodeType: number
  editable: boolean
  remarkRequired: boolean
  cancelAllowed: boolean
  transferAllowed: boolean
  actorType: number | null
  actorId: string[]
  notifierType: number | null
  notifierId: string[]
  preCriteriaList: ApprovalNodeCriteriaForm[]
  dashboardName: string
  dashboardId: string
}

interface ApprovalWorkflowRuleRaw {
  column?: string | null
  operator?: string | null
  value?: string | null
}

export interface ApprovalWorkflowRuleForm {
  column: string
  operator: string
  value: string
}

interface ApprovalWorkflowBranchRaw {
  id?: string | number | null
  priority?: number | null
  workflowRuleMode?: number | null
  rules?: ApprovalWorkflowRuleRaw[] | null
  nodes?: ApprovalWorkflowNodeRaw[] | null
}

export interface ApprovalWorkflowBranchForm {
  id: string
  priority: number
  workflowRuleMode: number
  rules: ApprovalWorkflowRuleForm[]
  nodes: ApprovalWorkflowNodeForm[]
}

interface ApprovalProcessDetailRaw {
  id?: string | number | null
  name?: string | null
  officeIds?: string | Array<string | number> | null
  canUndo?: number | null
  needConfirmPopupOnInitiate?: boolean | null
  documentConfigId?: string | number | null
  unknownProcessHandleMode?: number | null
  autoWriteOffAfterApproval?: number | null
  quickApprovalMode?: number | null
  msgNotifyEmail?: boolean | null
  msgNotifyBell?: boolean | null
  msgNotifyWeixin?: boolean | null
  msgNotifyEnterpriseWechat?: boolean | null
  runningState?: number | null
  remark?: string | null
  createdBy?: string | number | null
  creatorId?: string | number | null
  modifiedBy?: string | number | null
  modifierId?: string | number | null
  createTime?: string | null
  lastModified?: string | null
  formSubtype?: number | null
  templateUuid?: string | null
  exportType?: string | null
  workflows?: ApprovalWorkflowBranchRaw[] | null
}

export interface ApprovalProcessFormData {
  id?: string
  name: string
  officeIds: string[]
  canUndo: number
  documentConfigId: string
  unknownProcessHandleMode: number
  autoWriteOffAfterApproval: number
  quickApprovalMode: number
  needConfirmPopupOnInitiate: boolean
  msgNotifyEmail: boolean
  msgNotifyBell: boolean
  msgNotifyWeixin: boolean
  msgNotifyEnterpriseWechat: boolean
  runningState: number
  formSubtype: number | null
  templateUuid: string
  exportType: string
  remark: string
  workflows: ApprovalWorkflowBranchForm[]
}

export interface ApprovalProcessSavePayload {
  id?: string
  name: string
  officeIds: string[]
  canUndo: number
  documentConfigId: string
  remark?: string
  runningState?: number
  unknownProcessHandleMode: number
  autoWriteOffAfterApproval: number
  quickApprovalMode: number
  msgNotifyEmail: boolean
  msgNotifyBell: boolean
  msgNotifyWeixin: boolean
  msgNotifyEnterpriseWechat: boolean
  needConfirmPopupOnInitiate: boolean
  formSubtype?: number | null
  templateUuid?: string
  exportType?: string
  workflows: ApprovalWorkflowBranchForm[]
}

export interface ApprovalDelegation {
  id?: string
  fromUserId?: string
  toUserId?: string
}

export interface ApprovalDocumentConfigQuery {
  id?: string
  name?: string
  formType?: string
  type?: number
  tableName?: string
  code?: string
  businessService?: string
  businessType?: string
  enabled?: boolean
}

interface ApprovalDocumentConfigRaw {
  id?: string | number | null
  name?: string | null
  type?: number | null
  tableName?: string | null
  stateName?: string | null
  formType?: string | null
  defaultState?: number | null
  code?: string | null
  businessService?: string | null
  businessType?: string | null
  pageCode?: string | null
  formSubtype?: number | null
  enabled?: boolean | null
  remark?: string | null
  dateCreated?: string | null
}

export interface ApprovalDocumentConfig {
  id: string
  name: string
  type: number
  tableName: string
  stateName: string
  formType: string
  defaultState: number
  code: string
  businessService: string
  businessType: string
  pageCode: string
  formSubtype: number | null
  enabled: boolean
  remark: string
  dateCreated: string
}

const createLocalId = (prefix: string) => `${prefix}-${Math.random().toString(36).slice(2, 10)}`

const normalizeId = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) {
    return ''
  }
  return String(value)
}

const parseDelimitedIds = (value: string | Array<string | number> | null | undefined) => {
  if (Array.isArray(value)) {
    return value.map(item => normalizeId(item)).filter(Boolean)
  }
  if (!value) {
    return []
  }
  return value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
}

const normalizeCriteria = (criteria?: ApprovalNodeCriteriaRaw[] | null): ApprovalNodeCriteriaForm[] => {
  return (criteria || []).map(item => ({
    criteria: item.criteria || '',
    operator: item.operator || '',
    checkValue: item.checkValue || '',
    relation: item.relation || 1
  }))
}

const normalizeNode = (node?: ApprovalWorkflowNodeRaw | null): ApprovalWorkflowNodeForm => {
  const actorType = node?.actorType ?? null
  const notifierType = node?.notifierType ?? NO_NOTIFIER_TYPE
  const actorIds = parseDelimitedIds(node?.actorId)
  const notifierIds = parseDelimitedIds(node?.notifierId)

  return {
    id: createLocalId('node'),
    nodeName: node?.nodeName || '',
    nodeType: node?.nodeType ?? 0,
    editable: Boolean(node?.editable),
    remarkRequired: Boolean(node?.remarkRequired),
    cancelAllowed: Boolean(node?.cancelAllowed),
    transferAllowed: Boolean(node?.transferAllowed),
    actorType,
    actorId: actorType === 2 && actorIds.length === 0 ? [AUTO_RESOLVED_ACTOR_SENTINEL] : actorIds,
    notifierType,
    notifierId: notifierType === 2 && notifierIds.length === 0 ? [AUTO_RESOLVED_ACTOR_SENTINEL] : notifierIds,
    preCriteriaList: normalizeCriteria(node?.preCriteriaList),
    dashboardName: node?.dashboardName || '',
    dashboardId: normalizeId(node?.dashboardId)
  }
}

const normalizeBranch = (branch?: ApprovalWorkflowBranchRaw | null, index = 0): ApprovalWorkflowBranchForm => {
  const rules = (branch?.rules || []).map(rule => ({
    column: rule.column || '',
    operator: rule.operator || '',
    value: rule.value || ''
  }))

  return {
    id: createLocalId(`branch-${index + 1}`),
    priority: branch?.priority ?? index + 1,
    workflowRuleMode: branch?.workflowRuleMode ?? (rules.length > 0 ? 3 : 1),
    rules,
    nodes: (branch?.nodes || []).map(node => normalizeNode(node))
  }
}

const normalizeListItem = (item: ApprovalProcessListItemRaw): ApprovalProcessListItem => {
  const officeIds = parseDelimitedIds(item.officeIds)

  return {
    id: normalizeId(item.id),
    name: item.name || '',
    officeIds,
    officeIdsText: officeIds.join(','),
    documentConfigId: normalizeId(item.documentConfigId),
    runningState: item.runningState ?? 0,
    remark: item.remark || '',
    modifiedBy: normalizeId(item.modifierId ?? item.modifyId ?? item.modifyid ?? item.modifyBy ?? item.modifiedBy),
    lastModified: item.lastModified || '',
    createdBy: normalizeId(item.creatorId ?? item.createBy ?? item.createby ?? item.createdBy),
    createTime: item.createTime || '',
    formSubtype: item.formSubtype ?? null,
    templateUuid: item.templateUuid || '',
    exportType: item.exportType || ''
  }
}

const normalizePage = (page: ApprovalPageResponse<ApprovalProcessListItemRaw>): ApprovalPageResponse<ApprovalProcessListItem> => ({
  ...page,
  list: (page.list || []).map(item => normalizeListItem(item))
})

const normalizeDetail = (detail: ApprovalProcessDetailRaw): ApprovalProcessFormData => ({
  id: normalizeId(detail.id) || undefined,
  name: detail.name || '',
  officeIds: parseDelimitedIds(detail.officeIds),
  canUndo: detail.canUndo ?? -9,
  documentConfigId: normalizeId(detail.documentConfigId),
  unknownProcessHandleMode: detail.unknownProcessHandleMode ?? 2,
  autoWriteOffAfterApproval: detail.autoWriteOffAfterApproval ?? 0,
  quickApprovalMode: detail.quickApprovalMode ?? 1,
  needConfirmPopupOnInitiate: Boolean(detail.needConfirmPopupOnInitiate),
  msgNotifyEmail: Boolean(detail.msgNotifyEmail),
  msgNotifyBell: Boolean(detail.msgNotifyBell),
  msgNotifyWeixin: Boolean(detail.msgNotifyWeixin),
  msgNotifyEnterpriseWechat: Boolean(detail.msgNotifyEnterpriseWechat),
  runningState: detail.runningState ?? 0,
  formSubtype: detail.formSubtype ?? null,
  templateUuid: detail.templateUuid || '',
  exportType: detail.exportType || '',
  remark: detail.remark || '',
  workflows: (detail.workflows || []).map((branch, index) => normalizeBranch(branch, index))
})

const tryParseJson = (value: string | null | undefined): unknown => {
  if (!value) {
    return null
  }

  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

const normalizeRuleOptionItems = (
  items: unknown[],
  labelKeys = ['label', 'name', 'text', 'valueAlias', 'operatorAlias', 'chineseName', 'englishName', 'value', 'code', 'id'],
  valueKeys = ['value', 'operator', 'code', 'id', 'label', 'name']
): ApprovalRuleValueOption[] => {
  return items
    .map(item => {
      if (typeof item === 'string' || typeof item === 'number') {
        const normalized = String(item)
        return { label: normalized, value: normalized }
      }

      if (item && typeof item === 'object') {
        const rawItem = item as Record<string, unknown>
        const rawLabel = labelKeys.map(key => rawItem[key]).find(value => value !== undefined && value !== null && value !== '')
        const rawValue = valueKeys.map(key => rawItem[key]).find(value => value !== undefined && value !== null && value !== '')

        if (rawLabel === undefined || rawValue === undefined) {
          return null
        }

        return {
          label: String(rawLabel),
          value: String(rawValue)
        }
      }

      return null
    })
    .filter((item): item is ApprovalRuleValueOption => Boolean(item))
}

const parseRuleOperators = (operator: string | null | undefined): ApprovalRuleOperatorOption[] => {
  if (!operator) {
    return [{ label: '==', value: '==' }]
  }

  const parsedJson = tryParseJson(operator)
  if (Array.isArray(parsedJson)) {
    const parsedOptions = normalizeRuleOptionItems(
      parsedJson,
      ['operatorAlias', 'label', 'name', 'text', 'operator', 'value'],
      ['operator', 'value', 'code', 'id']
    )
    if (parsedOptions.length > 0) {
      return parsedOptions
    }
  }

  if (parsedJson && typeof parsedJson === 'object') {
    const payload = parsedJson as {
      options?: unknown[]
      items?: unknown[]
      data?: unknown[]
    }
    const source = payload.options || payload.items || payload.data
    if (Array.isArray(source)) {
      const parsedOptions = normalizeRuleOptionItems(
        source,
        ['operatorAlias', 'label', 'name', 'text', 'operator', 'value'],
        ['operator', 'value', 'code', 'id']
      )
      if (parsedOptions.length > 0) {
        return parsedOptions
      }
    }
  }

  const parsed = operator
    .split(/[,/|]/)
    .map(item => item.trim())
    .filter(Boolean)

  return (parsed.length > 0 ? parsed : [operator]).map(item => ({ label: item, value: item }))
}

const parseRuleValueMeta = (value: string | null | undefined): { options: ApprovalRuleValueOption[]; type: string } => {
  if (!value) {
    return { options: [], type: '' }
  }

  const parsed = tryParseJson(value)
  if (Array.isArray(parsed)) {
    return {
      options: normalizeRuleOptionItems(parsed, ['valueAlias', 'label', 'name', 'text', 'chineseName', 'englishName', 'value'], ['value', 'code', 'id', 'label']),
      type: 'enum'
    }
  }

  if (parsed && typeof parsed === 'object') {
    const payload = parsed as {
      type?: unknown
      options?: unknown[]
      items?: unknown[]
      data?: unknown[]
    }
    const source = payload.options || payload.items || payload.data
    return {
      options: Array.isArray(source)
        ? normalizeRuleOptionItems(source, ['valueAlias', 'label', 'name', 'text', 'chineseName', 'englishName', 'value'], ['value', 'code', 'id', 'label'])
        : [],
      type: typeof payload.type === 'string' ? payload.type : ''
    }
  }

  return {
    options: value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean)
      .map(item => ({ label: item, value: item })),
    type: 'enum'
  }
}

const parseRuleOptions = (value: string | null | undefined): ApprovalRuleValueOption[] => {
  return parseRuleValueMeta(value).options
}

const normalizeRuleDefinition = (item: ApprovalRuleDefinitionRaw): ApprovalRuleDefinition => {
  const operatorOptions = parseRuleOperators(item.operator)
  const valueMeta = parseRuleValueMeta(item.value)
  return {
    column: item.column || '',
    label: item.alias || item.column || '',
    operators: operatorOptions.map(option => option.value),
    operatorOptions,
    options: parseRuleOptions(item.value),
    customizeAllowed: Boolean(item.customizeAllowed),
    valueType: valueMeta.type,
    rawOperator: item.operator || '',
    rawValue: item.value || ''
  }
}

const sanitizeActorIds = (actorType: number | null, actorIds: string[]) => {
  if (actorType === 2) {
    return actorIds.length > 0 ? actorIds : [AUTO_RESOLVED_ACTOR_SENTINEL]
  }
  return actorIds.filter(Boolean)
}

const isNotifierTypeSpecified = (notifierType: number | null) =>
  notifierType !== null && notifierType !== NO_NOTIFIER_TYPE

const toSavePayload = (data: ApprovalProcessSavePayload) => ({
  id: data.id || undefined,
  name: data.name,
  officeIds: data.officeIds,
  canUndo: data.canUndo,
  documentConfigId: data.documentConfigId,
  remark: data.remark || '',
  runningState: data.runningState,
  unknownProcessHandleMode: data.unknownProcessHandleMode,
  autoWriteOffAfterApproval: data.autoWriteOffAfterApproval,
  quickApprovalMode: data.quickApprovalMode,
  msgNotifyEmail: data.msgNotifyEmail,
  msgNotifyBell: data.msgNotifyBell,
  msgNotifyWeixin: data.msgNotifyWeixin,
  msgNotifyEnterpriseWechat: data.msgNotifyEnterpriseWechat,
  needConfirmPopupOnInitiate: data.needConfirmPopupOnInitiate,
  formSubtype: data.formSubtype ?? undefined,
  templateUuid: data.templateUuid || undefined,
  exportType: data.exportType || undefined,
  workflows: data.workflows.map((branch, index) => {
    const rules = branch.rules.filter(rule => rule.column && rule.operator && rule.value)
    const workflowRuleMode = rules.length === 0 ? 1 : branch.workflowRuleMode

    return {
      priority: branch.priority || index + 1,
      workflowRuleMode,
      rules:
        workflowRuleMode === 1
          ? []
          : rules.map(rule => ({
            column: rule.column,
            operator: rule.operator,
            value: rule.value
          })),
      nodes: branch.nodes.map(node => {
        const notifierType = isNotifierTypeSpecified(node.notifierType) ? node.notifierType : null
        return {
          nodeName: node.nodeName,
          nodeType: node.nodeType,
          editable: node.editable,
          remarkRequired: node.remarkRequired,
          cancelAllowed: node.cancelAllowed,
          transferAllowed: node.transferAllowed,
          actorType: node.actorType,
          actorId: sanitizeActorIds(node.actorType, node.actorId),
          notifierType: notifierType ?? undefined,
          notifierId: notifierType === null ? [] : sanitizeActorIds(notifierType, node.notifierId),
          preCriteriaList: node.preCriteriaList
            .filter(item => item.criteria && item.operator && item.checkValue)
            .map(item => ({
              criteria: item.criteria,
              operator: item.operator,
              checkValue: item.checkValue,
              relation: item.relation || 1
            })),
          dashboardName: node.dashboardName || undefined,
          dashboardId: node.dashboardId || undefined
        }
      })
    }
  })
})

const normalizeDocumentConfig = (item: ApprovalDocumentConfigRaw): ApprovalDocumentConfig => ({
  id: normalizeId(item.id),
  name: item.name || '',
  type: item.type ?? 0,
  tableName: item.tableName || item.businessService || item.code || '',
  stateName: item.stateName || 'state',
  formType: item.formType || '',
  defaultState: item.defaultState ?? 0,
  code: item.code || item.tableName || item.businessService || item.name || '',
  businessService: item.businessService || item.tableName || '',
  businessType: item.businessType || '普通',
  pageCode: item.pageCode || '',
  formSubtype: item.formSubtype ?? null,
  enabled: item.enabled !== false,
  remark: item.remark || '',
  dateCreated: item.dateCreated || ''
})

const buildDocumentConfigQuery = (query: ApprovalDocumentConfigQuery) => ({
  ...(query.id ? { id: query.id } : {}),
  ...(query.name ? { name: query.name } : {}),
  ...(query.formType ? { formType: query.formType } : {}),
  ...(query.type !== undefined ? { type: query.type } : {}),
  ...(query.tableName ? { tableName: query.tableName } : {}),
  ...(query.code ? { tableName: query.code } : {}),
  ...(query.businessService ? { tableName: query.businessService } : {})
})

export const listApprovalProcesses = async(data: ApprovalProcessQuery) => {
  const response = await request.post<ApprovalPageResponse<ApprovalProcessListItemRaw>>(`${API_BASE}/config/list`, data)
  return normalizePage(response)
}

export const getApprovalProcessDetail = async(workflowGroupId: string) => {
  const response = await request.get<ApprovalProcessDetailRaw>(`${API_BASE}/config/detail`, { workflowGroupId })
  return normalizeDetail(response)
}

export const saveApprovalProcess = (data: ApprovalProcessSavePayload) => {
  return request.post<boolean>(`${API_BASE}/config/workflow`, toSavePayload(data))
}

export const updateApprovalProcessStatus = (workflowGroupId: string, runningState: number) => {
  return request.post<boolean>(`${API_BASE}/config/active`, { workflowGroupId, runningState })
}

export const deleteApprovalProcess = (workflowGroupId: string) => {
  return request.post<boolean>(`${API_BASE}/config/active`, {
    workflowGroupId,
    runningState: WORKFLOW_HIDDEN_DISABLED_STATE
  })
}

export const copyApprovalProcess = (workflowGroupId: string) => {
  return request.post<boolean>(`${API_BASE}/config/copy`, { workflowGroupId })
}

export const getApprovalProcessRuleDefinitions = async(documentConfigId: string) => {
  const response = await request.get<ApprovalRuleDefinitionRaw[]>(`${API_BASE}/config/rule`, { documentConfigId })
  return (response || []).map(item => normalizeRuleDefinition(item))
}

export const getApprovalProcessGroupNames = () => {
  return request.get<Array<{ name: string }>>(`${API_BASE}/config/workflowGroupNameList`)
}

export const listApprovalDelegations = async() => {
  const response = await request.get<Array<{ id?: string | number; fromUserId?: string | number; toUserId?: string | number }>>(
    `${API_BASE}/config/appoint/list`
  )

  return (response || []).map(item => ({
    id: normalizeId(item.id) || undefined,
    fromUserId: normalizeId(item.fromUserId) || undefined,
    toUserId: normalizeId(item.toUserId) || undefined
  }))
}

export const saveApprovalDelegations = (data: ApprovalDelegation[]) => {
  return request.post<boolean>(`${API_BASE}/config/appoint/save`, data)
}

export const checkApprovalProcessExists = (officeId: string, formSubtype: number) => {
  return request.get<boolean>(`${API_BASE}/config/existWorkflowGroup`, { officeId, formSubtype })
}

export const listApprovalDocumentConfigs = async(query: ApprovalDocumentConfigQuery = {}) => {
  const response = await request.post<ApprovalDocumentConfigRaw[]>(`${SERVICE_API_BASE}/document/config/list`, buildDocumentConfigQuery(query))
  return (response || []).map(item => normalizeDocumentConfig(item))
}

export const listApprovalPlatformDocumentConfigs = async(officeIds: string[]) => {
  const response = await request.get<ApprovalDocumentConfigRaw[]>(`${SERVICE_API_BASE}/api/document/platform/config/getFormList`, {
    officeIds: officeIds.join(',')
  })
  return (response || []).map(item => normalizeDocumentConfig(item))
}

export const approvalProcessActorSentinel = AUTO_RESOLVED_ACTOR_SENTINEL
export const approvalProcessNoNotifierType = NO_NOTIFIER_TYPE
